"use strict";
/**
 * The hockey pad at /app/win-prob/nhl/<gameId>/pad (docs/nhl-model/PLAN.md §4.5).
 *
 * Every button is a state-changing event: stamped on pointerdown with the
 * touch's own time plus the clock offset (as the football tap pad does),
 * queued, sent one at a time with an idempotent id, and the server's reply
 * (the game's new view) repaints the banner at once. Between replies the
 * page polls the view every 2 s with `?presence=pad`, which is also its
 * heartbeat, and runs the clock locally off the anchor.
 *
 * Upright, the page is the information view: every 15 s it also fetches the
 * game's series (the box's database: the book, the model, each tap's market
 * reaction) for the chart and the event log.
 *
 * Timers here are this view's own (poll, series, flush, clock sync, banner
 * tick) — not market polling (ai/GOTCHAS.md §2); started in openHockeyPad,
 * stopped in closeHockeyPad, never touching the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GATE_MS = void 0;
exports.setupHockeyPad = setupHockeyPad;
exports.openHockeyPad = openHockeyPad;
exports.closeHockeyPad = closeHockeyPad;
exports.isHockeyPadOpen = isHockeyPadOpen;
const win_prob_1 = require("../api/win-prob");
const request_1 = require("../api/request");
const hockey_event_queue_1 = require("../services/hockey-event-queue");
const tap_queue_1 = require("../services/tap-queue");
const hockey_pad_1 = require("../ui/hockey-pad");
const tap_pad_1 = require("../ui/tap-pad");
const hockey_events_1 = require("../../shared/hockey-events");
const uuid_1 = require("../services/uuid");
const win_prob_2 = require("../types/win-prob");
const POLL_MS = 2000;
/** The series (chart + reactions) refresh; a tap's reaction needs the book 30 s on, so faster buys nothing. */
const SERIES_MS = 15000;
const FLUSH_MS = 500;
const TICK_MS = 250;
const SYNC_MS = 60000;
const SYNC_SAMPLES = 5;
const REFUSAL_MS = 3000;
/** The landscape swap cells (ui/hockey-pad.ts renderHockeyLandscape): presses on a cell's swapping buttons within this of its swap are suppressed, as the NBA pad's FT area (N8 §11.1). Monotonic time. */
exports.GATE_MS = 250;
let pad = null;
const body = () => document.getElementById("win-prob-body");
const q = (sel) => body()?.querySelector(sel) ?? null;
const serverNow = (p) => Date.now() + (p.clock?.offsetMs ?? 0);
function teamsOf(view, fallback) {
    const l = view?.listing;
    return l ? { home: l.home.abbreviation, away: l.away.abbreviation } : fallback;
}
function history(p) {
    const events = p.view?.events ?? [];
    const undone = new Set(events.filter((e) => e.reversesSeq !== null).map((e) => e.reversesSeq));
    const out = events
        .filter((e) => e.reversesSeq === null)
        .map((e) => ({ seq: e.seq, kind: e.kind, side: e.side, value: e.value, atMs: Date.parse(e.ts), undone: undone.has(e.seq), pending: false, feed: e.source === "feed" }));
    for (const e of p.queue.unsent)
        out.push({ seq: null, kind: e.kind, side: e.side, value: e.value, atMs: Date.parse(e.clientTs), undone: false, pending: true });
    return out;
}
function paintBanner(p) {
    const banner = q(".hp-banner");
    if (banner)
        banner.innerHTML = (0, hockey_pad_1.renderHockeyBanner)(p.view, p.teams, serverNow(p), p.queue.size);
    body()?.querySelectorAll(".hp-kill").forEach((kill) => {
        kill.textContent = p.view?.killed ? "RESUME" : "KILL";
        kill.classList.toggle("hp-killed", p.view?.killed === true);
    });
    paintLandscape(p);
    const st = p.view?.latest?.state;
    const hockey = (0, win_prob_2.isWpHockeyState)(st) ? st : null;
    // Colour only — the grid must not move under a finger.
    body()?.querySelectorAll(".hp-so").forEach((b) => b.classList.toggle("hp-off", !hockey?.shootout));
    body()?.querySelectorAll(".hp-confirm, .hp-overturn").forEach((b) => b.classList.toggle("hp-off", !hockey?.pendingGoal));
    const chance = hockey?.chance && hockey.chance.untilMs > serverNow(p) ? hockey.chance.side : null;
    body()?.querySelectorAll(".hp-chance-btn").forEach((b) => b.classList.toggle("hp-on", b.dataset.side === chance));
    // HOLD: the cell swaps with the state; both buttons are off while play runs (PLAN.md §5.3).
    const held = hockey ? (0, hockey_pad_1.holdActive)(hockey, serverNow(p)) : false;
    const holdOk = hockey ? (0, hockey_pad_1.holdAllowed)(hockey) : false;
    body()?.querySelectorAll(".hp-hold-cell").forEach((cell) => {
        swapTo(p, cell, held ? "held" : "idle");
        cell.querySelectorAll(".tap-btn").forEach((b) => { b.disabled = !holdOk; b.classList.toggle("hp-off", !holdOk); });
        const off = cell.querySelector(".hp-hold-off");
        if (off && hockey)
            off.innerHTML = held ? `HOLD OFF<small>${(0, hockey_pad_1.holdLeft)(hockey, serverNow(p))}</small>` : "HOLD OFF"; // digits and a colon only
    });
}
function paintStatus(p) {
    const status = q(".tap-pad-status");
    if (!status)
        return;
    const refused = p.refusal && p.refusal.until > Date.now() ? p.refusal.text : null;
    status.classList.toggle("tap-refused", refused !== null);
    const stale = p.lastPollOk !== null && Date.now() - p.lastPollOk > 3 * POLL_MS;
    const landscape = q(".hp-l-status");
    if (landscape) {
        landscape.classList.toggle("tap-refused", refused !== null);
        landscape.textContent = refused ?? `${p.queue.size ? `${p.queue.size} unsent` : "all sent"}${p.clock ? ` · clock ${p.clock.offsetMs >= 0 ? "+" : ""}${Math.round(p.clock.offsetMs)} ms ±${Math.round(p.clock.rttMs / 2)}` : " · clock unsynced"}${p.queue.lastError ? ` · ${p.queue.lastError}` : stale ? " · server not answering" : ""}`;
    }
    status.innerHTML = refused ?? (0, tap_pad_1.renderTapStatus)({
        pending: p.queue.size,
        offsetMs: p.clock?.offsetMs ?? null,
        rttMs: p.clock?.rttMs ?? null,
        syncedAgoS: p.clock ? Math.round((Date.now() - p.clock.at) / 1000) : null,
        error: p.queue.lastError ?? (stale ? "server not answering" : null),
        conflicts: p.queue.refused.length,
    });
    const hist = q(".tap-history");
    if (hist)
        hist.innerHTML = (0, hockey_pad_1.renderHockeyHistory)(history(p), p.teams, undefined, p.reactions);
}
/** The portrait view's chart and summary line, from the last series fetched. */
function paintSeries(p) {
    const chart = q(".hp-chart");
    if (chart)
        chart.innerHTML = (0, hockey_pad_1.renderHockeyChart)(p.series, p.teams);
    const summary = q(".hp-series-summary");
    if (summary)
        summary.innerHTML = (0, hockey_pad_1.renderSeriesSummary)(p.series);
}
async function loadSeries(p) {
    if (document.hidden)
        return;
    try {
        const series = await (0, win_prob_1.fetchHockeySeries)(p.gameId);
        if (pad !== p)
            return;
        p.series = series;
        p.reactions = new Map((series?.events ?? []).map((e) => [e.seq, e]));
        paintSeries(p);
        paintStatus(p);
    }
    catch {
        // the next tick tries again; the chart keeps what it has
    }
}
function paintPregame(p) {
    const panel = q(".hp-pregame");
    if (!panel)
        return;
    const st = p.view?.latest?.state;
    const open = p.setupOpen || ((0, win_prob_2.isWpHockeyState)(st) ? st.status === "pre" : p.view !== null && !p.view.latest);
    panel.classList.toggle("hp-pregame-open", open);
    if (!open) {
        panel.innerHTML = "";
        return;
    }
    if (!p.draft)
        p.draft = (0, hockey_pad_1.draftFromOdds)(p.view?.latest?.pregame.odds, (0, win_prob_2.isWpHockeyState)(st) ? st.gameType : "regular");
    if (!panel.firstChild)
        panel.innerHTML = (0, hockey_pad_1.renderPregamePanel)(p.draft, p.view, p.fit, p.pregameMessage, p.teams);
    else {
        // Only the read-outs re-render; the inputs keep what the operator typed.
        const fresh = document.createElement("div");
        fresh.innerHTML = (0, hockey_pad_1.renderPregamePanel)(p.draft, p.view, p.fit, p.pregameMessage, p.teams);
        for (const sel of [".hp-fit", ".hp-anchors", ".hp-anchor-actions"]) {
            const box = panel.querySelector(sel);
            if (box)
                box.innerHTML = fresh.querySelector(sel)?.innerHTML ?? "";
        }
    }
}
/** Set a swap cell's mode; a change stamps the gate and shows it for GATE_MS. */
function swapTo(p, cell, mode) {
    if (cell.dataset.mode === mode)
        return;
    cell.dataset.mode = mode;
    const key = `${cell.dataset.swap}:${cell.dataset.side ?? ""}`;
    p.swappedAt[key] = performance.now();
    cell.classList.add("hp-gated");
    p.timers.push(window.setTimeout(() => cell.classList.remove("hp-gated"), exports.GATE_MS));
}
/** The landscape layout's live parts; the grid itself never re-renders (buttons fire on pointerdown). */
function paintLandscape(p) {
    const root = q(".hp-landscape");
    if (!root)
        return;
    const paint = (0, hockey_pad_1.landscapePaint)(p.view, p.teams, serverNow(p));
    for (const side of ["home", "away"]) {
        const half = root.querySelector(`.hp-half-${side}`);
        if (!half)
            continue;
        const set = (sel, html) => { const el = half.querySelector(sel); if (el)
            el.innerHTML = html; };
        set(".hp-l-prob", paint.prob[side]);
        set(".hp-l-touch", paint.touch[side]);
        set(".hp-l-paper", paint.paper[side]);
    }
    const time = root.querySelector(".hp-l-time"), sub = root.querySelector(".hp-l-sub");
    if (time)
        time.textContent = paint.time;
    if (sub)
        sub.textContent = paint.sub;
    const badge = root.querySelector(".hp-l-quote");
    if (badge) {
        badge.className = `hp-l-quote ${paint.quote.cls}`;
        badge.textContent = paint.quote.text;
    }
    const st = p.view?.latest?.state;
    const hockey = (0, win_prob_2.isWpHockeyState)(st) ? st : null;
    root.querySelectorAll('.hp-swap[data-swap="review"]').forEach((cell) => swapTo(p, cell, hockey?.pendingGoal === cell.dataset.side ? "pending" : "idle"));
    root.querySelectorAll('.hp-swap[data-swap="goalie"]').forEach((cell) => swapTo(p, cell, hockey?.goaliePulled[cell.dataset.side] ? "out" : "in"));
    // The clock state colours the centre column; START is off where it would apply nothing (intermission, final), STOP while stopped.
    const centre = root.querySelector(".hp-l-centre");
    if (centre)
        centre.dataset.run = paint.run;
    const startOff = hockey ? hockey.status === "intermission" || hockey.status === "final" : true;
    root.querySelectorAll(".hp-l-start").forEach((b) => b.classList.toggle("hp-off", startOff));
    root.querySelectorAll(".hp-l-stop").forEach((b) => b.classList.toggle("hp-off", !hockey?.clock.running));
}
function refresh(p) {
    paintBanner(p);
    paintStatus(p);
    paintPregame(p);
    paintSeries(p);
}
function refuse(p, text) {
    p.refusal = { text, until: Date.now() + REFUSAL_MS };
    navigator.vibrate?.([40, 40, 40]);
    paintStatus(p);
}
function takeView(p, view) {
    p.view = view;
    p.teams = teamsOf(view, p.teams);
    p.lastPollOk = Date.now();
    refresh(p);
}
async function poll(p) {
    try {
        const view = await (0, win_prob_1.fetchWinProbGame)("nhl", p.gameId, { presence: "pad" });
        if (pad === p)
            takeView(p, view);
    }
    catch (err) {
        if (pad !== p)
            return;
        if (err instanceof request_1.ApiRequestError && err.statusCode === 404) {
            // Not tracked (a reload after the server restarted): track it and poll again.
            try {
                await (0, win_prob_1.trackWinProbGame)("nhl", p.gameId);
            }
            catch {
                // the next poll tries again
            }
        }
        paintStatus(p);
    }
}
async function syncClock(p) {
    const samples = [];
    for (let i = 0; i < SYNC_SAMPLES; i++) {
        try {
            const t0 = Date.now();
            const serverMs = await (0, win_prob_1.fetchServerClock)();
            samples.push({ t0, t1: Date.now(), serverMs });
        }
        catch {
            break;
        }
    }
    const best = (0, tap_queue_1.estimateClockOffset)(samples);
    if (best && pad === p)
        p.clock = { ...best, at: Date.now() };
    paintStatus(p);
}
function flush(p) {
    void p.queue.flush().then(() => {
        if (pad === p)
            refresh(p);
    });
}
function send(p, kind, side, value, atMs) {
    const event = {
        clientEventId: (0, uuid_1.uuid)(),
        kind,
        side: hockey_events_1.SIDED_HOCKEY_KINDS.has(kind) ? side : null,
        value,
        clientTs: new Date(atMs + (p.clock?.offsetMs ?? 0)).toISOString(),
    };
    p.refusal = null;
    p.queue.add(event);
    navigator.vibrate?.(15);
    paintStatus(p);
    flush(p);
}
/** pointerdown on a pad button: stamp, queue, send. */
function onPointerDown(event) {
    if (!pad)
        return;
    const btn = event.target.closest(".tap-btn[data-ev-kind]");
    const kind = btn?.dataset.evKind;
    if (!btn || !(0, hockey_events_1.isHockeyEventKind)(kind))
        return;
    event.preventDefault();
    const atMs = performance.timeOrigin + event.timeStamp;
    const side = btn.dataset.side ?? null;
    const value = btn.dataset.value === undefined ? null : Number(btn.dataset.value);
    if (hockey_events_1.SIDED_HOCKEY_KINDS.has(kind) && !side)
        return refuse(pad, `${kind} needs a team`);
    // The swap gate: a press on a cell's swapping button within GATE_MS of its swap is not sent and not recorded.
    const cell = btn.classList.contains("hp-swaps") ? btn.closest(".hp-swap") : null;
    if (cell && performance.now() - (pad.swappedAt[`${cell.dataset.swap}:${cell.dataset.side ?? ""}`] ?? -Infinity) < exports.GATE_MS)
        return;
    // A tap in the MORE sheet closes it, except the clock corrections.
    const sheet = btn.closest(".hp-more");
    if (sheet && !btn.classList.contains("hp-keep-open"))
        sheet.hidden = true;
    btn.classList.add("tap-flash");
    window.setTimeout(() => btn.classList.remove("tap-flash"), 150);
    send(pad, kind, side, value, atMs);
}
function onClick(event) {
    const p = pad;
    if (!p)
        return;
    const target = event.target;
    if (target.closest(".hp-exit"))
        return p.onExit();
    if (target.closest(".hp-kill")) {
        const on = !(p.view?.killed === true);
        void (0, win_prob_1.setKillSwitch)(on).then(() => poll(p)).catch((err) => refuse(p, `kill switch not confirmed: ${err.message}`));
        return;
    }
    if (target.closest(".hp-more-open")) {
        const sheet = q(".hp-more");
        if (sheet)
            sheet.hidden = false;
        return;
    }
    if (target.closest(".hp-more-close")) {
        const sheet = q(".hp-more");
        if (sheet)
            sheet.hidden = true;
        return;
    }
    if (target.closest(".hp-set")) {
        const input = target.closest(".hp-clock-set")?.querySelector(".hp-clock-input") ?? q(".hp-clock-input");
        const secs = input ? (0, hockey_pad_1.parseClockInput)(input.value) : null;
        if (secs === null)
            return refuse(p, "clock: type mm:ss");
        send(p, "clock_set", null, secs, Date.now());
        return;
    }
    if (target.closest(".hp-setup-close")) {
        p.setupOpen = false;
        paintPregame(p);
        return;
    }
    const anchorBtn = target.closest(".hp-anchor");
    if (anchorBtn) {
        const anchor = anchorBtn.dataset.anchor === "exchange" ? "exchange" : "book";
        p.pregameMessage = "solving…";
        paintPregame(p);
        void (0, win_prob_1.anchorHockeyGame)(p.gameId, anchor, p.draft?.gameType ?? "regular")
            .then(({ fit, view }) => {
            if (pad !== p)
                return;
            p.fit = fit;
            p.pregameMessage = null;
            if (view)
                takeView(p, view);
            else
                refresh(p);
        })
            .catch((err) => {
            if (pad !== p)
                return;
            p.pregameMessage = `not re-anchored: ${err.message}`;
            paintPregame(p);
        });
        return;
    }
    if (target.closest(".hp-recalibrate")) {
        if (!p.draft)
            return;
        const parsed = (0, hockey_pad_1.oddsFromDraft)(p.draft);
        if (parsed.error !== undefined) {
            p.pregameMessage = parsed.error;
            paintPregame(p);
            return;
        }
        p.pregameMessage = "solving…";
        paintPregame(p);
        void (0, win_prob_1.calibrateHockeyGame)(p.gameId, parsed.odds, p.draft.gameType)
            .then(({ fit, view }) => {
            if (pad !== p)
                return;
            p.fit = fit;
            p.pregameMessage = null;
            if (view)
                takeView(p, view);
            else
                refresh(p);
        })
            .catch((err) => {
            if (pad !== p)
                return;
            p.pregameMessage = `not calibrated: ${err.message}`;
            paintPregame(p);
        });
        return;
    }
    if (target.closest(".hp-banner")) {
        p.setupOpen = !p.setupOpen;
        paintPregame(p);
    }
}
function onInput(event) {
    const p = pad;
    if (!p?.draft)
        return;
    const el = event.target;
    if (el.classList.contains("hp-odds")) {
        const field = el.dataset.field;
        if (field && field !== "gameType")
            p.draft[field] = el.value;
    }
    else if (el.classList.contains("hp-game-type")) {
        p.draft.gameType = el.value === "playoff" ? "playoff" : "regular";
    }
}
const onOnline = () => {
    if (pad)
        flush(pad);
};
/** Once, from setupWinProbPage: the listeners sit on the shared page body. */
function setupHockeyPad() {
    body()?.addEventListener("pointerdown", onPointerDown);
    body()?.addEventListener("click", onClick);
    body()?.addEventListener("input", onInput);
    body()?.addEventListener("change", onInput);
    window.addEventListener("online", onOnline);
}
function openHockeyPad(gameId, onExit) {
    closeHockeyPad();
    const current = {
        gameId,
        queue: null,
        view: null,
        teams: { home: "HOME", away: "AWAY" },
        clock: null,
        refusal: null,
        draft: null,
        fit: null,
        pregameMessage: null,
        setupOpen: false,
        lastPollOk: null,
        series: null,
        reactions: new Map(),
        timers: [],
        swappedAt: {},
        onExit,
    };
    current.queue = new hockey_event_queue_1.HockeyEventQueue(`hockey-pad:nhl:${gameId}`, (event) => (0, win_prob_1.postHockeyEvent)(gameId, event).catch((err) => {
        if (err instanceof request_1.ApiRequestError && err.statusCode >= 400 && err.statusCode < 500 && err.statusCode !== 401) {
            throw { refused: true, message: err.message };
        }
        throw err;
    }), hockey_event_queue_1.browserEventStorage, (view) => {
        if (pad === current)
            takeView(current, view);
    });
    pad = current;
    const root = body();
    if (root)
        root.innerHTML = (0, hockey_pad_1.renderHockeyPad)(current.teams, `NHL ${gameId}`);
    refresh(current);
    void (0, win_prob_1.trackWinProbGame)("nhl", gameId).catch(() => undefined).then(() => poll(current)).then(() => {
        if (pad !== current)
            return;
        if (current.view?.listing)
            body()?.querySelectorAll(".tap-pad-title, .hp-l-title").forEach((t) => { t.textContent = `${current.teams.away} @ ${current.teams.home}`; });
        // Team names arrive with the first view; the grid is rendered once, so rebuild it now, before any tap.
        const r = body();
        if (r && current.view?.listing) {
            r.innerHTML = (0, hockey_pad_1.renderHockeyPad)(current.teams, `${current.teams.away} @ ${current.teams.home}`);
            refresh(current);
        }
        void loadSeries(current); // after the track: the series is 404 until then
    });
    void syncClock(current);
    current.timers.push(window.setInterval(() => void poll(current), POLL_MS), window.setInterval(() => void loadSeries(current), SERIES_MS), window.setInterval(() => flush(current), FLUSH_MS), window.setInterval(() => paintBanner(current), TICK_MS), window.setInterval(() => void syncClock(current), SYNC_MS));
    flush(current); // events left unsent before a reload go out now
}
function closeHockeyPad() {
    if (!pad)
        return;
    for (const t of pad.timers) {
        window.clearInterval(t);
        window.clearTimeout(t);
    }
    void pad.queue.flush();
    pad = null;
}
function isHockeyPadOpen() {
    return pad !== null;
}
